import type React from 'react'
import { Loader2 } from 'lucide-react'
import { ChatPane } from '@/components/layout/chat-pane'
import { useChatSurfaceRegistry } from '@/hooks/use-chat-surface-registry'
import { useHostedChatThread } from '@/hooks/use-hosted-chat-thread'
import { testIds } from '@/lib/test-ids'
import { shouldCoverHostedThreadResolving, wasPromotedFromNewThread } from '@/components/layout/chat-pane.utils'

type ChatMainTabProps = {
  threadId: string
  title?: string
  onDocumentClick?: (documentId: string, eventOrBoolean?: React.MouseEvent | boolean) => void
  onFolderClick?: (folderPath: string) => void
  openTabIds?: string[]
  recentlyViewedIds?: string[]
}

/** Editor-tab chat surface; resolves linked ai_conversation via thread metadata. */
export function ChatMainTab({
  threadId,
  title,
  onDocumentClick,
  onFolderClick,
  openTabIds = [],
  recentlyViewedIds = [],
}: ChatMainTabProps) {
  const registry = useChatSurfaceRegistry()

  const { linkedConversationId, resolvedTitle, resolving } = useHostedChatThread(threadId, {
    initialTitle: title ?? 'New chat',
    onTitleResolved: registry.registerThreadTitle,
    onThreadIdResolved: (fromThreadId, toThreadId) => {
      registry.migrateThreadId(fromThreadId, toThreadId)
    },
  })

  const coverResolving = shouldCoverHostedThreadResolving({
    resolving,
    sawNewThreadPlaceholder: wasPromotedFromNewThread(threadId),
  })

  return (
    <div className="relative flex h-full min-h-0 flex-col" data-testid={testIds.chatMainTab.container}>
      {coverResolving ? (
        <div
          className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-background/60"
          data-testid={testIds.chatMainTab.loading}
        >
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : null}
      <ChatPane
        hostedThreadId={threadId}
        hostedThreadTitle={resolvedTitle}
        hostedLinkedConversationId={linkedConversationId}
        hostedThreadResolving={resolving}
        surfaceVariant="editor"
        activeDocumentId={`chat-conversation-${threadId}`}
        activeDocumentTitle={resolvedTitle}
        onDocumentClick={onDocumentClick}
        onFolderClick={onFolderClick}
        openTabIds={openTabIds}
        recentlyViewedIds={recentlyViewedIds}
      />
    </div>
  )
}
